import { gql, useQuery } from "@apollo/client";
import { FormDataType } from "..";
import { Product } from "../../../../graphql/operations";
import ErrorScreen from "../../../Atoms/ErrorScreen";
import Input from "../../../Atoms/Input";
import Loader from "../../../Atoms/Loader";
import OptionsSelector from "../SelectMenus/OptionsSelector";
import * as options from "./contactFormData";

type ContactFormFieldsProps = {
  fields: (keyof FormDataType)[];
  formData: FormDataType;
  onInputChange: (field: keyof FormDataType, value: string) => void;
  selectedCategory: string;
};

export default function ContactFormFields({
  fields,
  formData,
  onInputChange,
  selectedCategory,
}: ContactFormFieldsProps) {
  const { data, loading, error } = useQuery(GET_PRODUCTS, {
    variables: { lang: "en" },
  });

  if (loading)
    return (
      <Loader
        style={{
          width: 36,
          height: 36,
        }}
      />
    );
  if (error) return <ErrorScreen />;

  const productNames = data.products.records
    .map((record: Product) => record.translation.name)
    .sort();

  const formatFieldName = (field: string, category?: string) => {
    if (
      field === "company_url" &&
      category === "Business & Partnership Inquiry"
    ) {
      return "Website or Social Media URL";
    }
    if (field === "company_url" && category === "Media Inquiries (取材)") {
      return "Website";
    }
    return field
      .replace(/_/g, " ")
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };

  return (
    <>
      {fields
        .filter((field) => field !== "is_connected_to_bokun")
        .map((field) => (
          <div key={field as string} style={{ marginBottom: "20px" }}>
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                gap: "15px",
              }}
            >
              <h3 style={{ marginBottom: "5px" }}>
                {formatFieldName(field, selectedCategory)}
                {!(field === "booking_id" && selectedCategory === "Other") && (
                  <RequiredMarker />
                )}
              </h3>
            </div>
            {field === "tour_name" ? (
              <OptionsSelector
                value={formData[field] || ""}
                options={productNames}
                onChange={(value) => onInputChange(field, value)}
                placeholder="Select a Product"
              />
            ) : field === "number_of_guests" ? (
              <Input
                type="number"
                value={formData[field] || ""}
                onChange={(e) => {
                  const value = e.target.value;
                  if (/^\d*$/.test(value)) {
                    onInputChange(field, value);
                  }
                }}
                placeholder={`Enter ${formatFieldName(field)}`}
                required
              />
            ) : field === "requested_tour_date" ? (
              <Input
                type="date"
                value={formData[field] || ""}
                onChange={(e) => onInputChange(field, e.target.value)}
                required
              />
            ) : field === "inquiry_details" &&
              selectedCategory in options.inquiryDetailsOptions ? (
              <OptionsSelector
                value={formData[field] || ""}
                options={options.inquiryDetailsOptions[selectedCategory]}
                placeholder={`Select ${formatFieldName(field)}`}
                onChange={(value) => onInputChange(field, value)}
              />
            ) : field === "message" ? (
              <>
                <Input
                  value={formData[field] || ""}
                  onChange={(e) => onInputChange(field, e.target.value)}
                  placeholder={`Enter ${formatFieldName(field)}`}
                  multiline={true}
                />
                <p
                  style={{
                    color: formData.message?.length > 500 ? "red" : "grey",
                    fontSize: "15px",
                  }}
                >
                  {formData.message?.length || 0}/500 characters
                </p>
              </>
            ) : field === "type_of_partnership" ? (
              <>
                <OptionsSelector
                  value={formData[field] || ""}
                  options={options.typeOfPartnership}
                  placeholder="Select a partnership type"
                  onChange={(value) => onInputChange(field, value)}
                />
                {formData.type_of_partnership === "OTA" && (
                  <div style={{ marginTop: "20px" }}>
                    <h3 style={{ marginBottom: "5px" }}>
                      Are you connected to Bokun?
                      <RequiredMarker />
                    </h3>
                    <OptionsSelector
                      value={formData.is_connected_to_bokun || ""}
                      options={options.isConnectedToBokunOptions}
                      placeholder="Select an answer"
                      onChange={(value) =>
                        onInputChange("is_connected_to_bokun", value)
                      }
                    />
                  </div>
                )}
              </>
            ) : field === "type_of_promotion" ? (
              <OptionsSelector
                value={formData[field] || ""}
                options={options.typeOfPromotion}
                placeholder="Select a promotion type"
                onChange={(value) => onInputChange(field, value)}
              />
            ) : (
              <Input
                value={formData[field] || ""}
                onChange={(e) => onInputChange(field, e.target.value)}
                placeholder={`Enter ${formatFieldName(
                  field,
                  selectedCategory
                )}`}
              />
            )}
          </div>
        ))}
    </>
  );
}

const GET_PRODUCTS = gql`
  query Products($lang: Iso639!) {
    products {
      records {
        translation(lang: $lang) {
          name
        }
      }
    }
  }
`;

function RequiredMarker() {
  return <span style={{ color: "red" }}>*</span>;
}
